// src/app/core/chatPage/chatCopmonents/chatInForm/components/InputArea.tsx
import { useRef, useEffect, useState } from "react";
import { Send, Square, Paperclip, X, File as FileIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { motion, AnimatePresence } from "motion/react";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";
import { TextGenerationViewModel } from "../../shared/TextGenerationViewModel";

interface InputAreaProps {
  onSend: (text: string) => void;
  onStop: () => void;
}

export const InputArea = observer(function InputArea({
  onSend,
  onStop,
}: InputAreaProps) {
  const { t } = useTranslation();
  const vm = TextGenerationViewModel.getInstance();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.min(Math.max(scrollHeight, 24), 200)}px`;
    }
  }, [vm.draftText]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if ((!vm.draftText.trim() && vm.attachedFiles.length === 0) || vm.isGenerating) return;
    onSend(vm.draftText);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      vm.addFiles(Array.from(e.target.files));
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      vm.addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const hasContent = vm.draftText.trim().length > 0 || vm.attachedFiles.length > 0;

  return (
    <div
      className="w-full shrink-0 px-[var(--container-padding,16px)] pt-2"
      style={{
        flexShrink: 0,
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: `max(16px, env(safe-area-inset-bottom, 0px))`,
      }}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
    >
      <div className="mx-auto max-w-[var(--container-max-width,672px)] relative">
        
        {/* Превью прикрепленных файлов */}
        <AnimatePresence>
          {vm.attachedFiles.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="flex flex-wrap gap-2 mb-2 p-2 bg-white/50 dark:bg-white/5 rounded-2xl backdrop-blur-md"
            >
              {vm.attachedFiles.map((file, idx) => (
                <div key={idx} className="relative flex items-center gap-2 p-1.5 pr-8 bg-white dark:bg-[#2C2C2E] rounded-xl border border-gray-200 dark:border-white/10 max-w-[200px]">
                  {file.type.startsWith('image/') ? (
                    <img src={URL.createObjectURL(file)} alt="preview" className="w-8 h-8 rounded object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-gray-100 dark:bg-white/10 flex items-center justify-center shrink-0">
                      <FileIcon size={16} className="text-gray-500" />
                    </div>
                  )}
                  <span className="text-xs truncate text-foreground/80">{file.name}</span>
                  <button 
                    onClick={() => vm.removeFile(idx)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded-full"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <div className={`flex items-end gap-2 bg-white/80 dark:bg-[#1C1C1E]/90 backdrop-blur-xl rounded-3xl border shadow-sm transition-colors p-1.5 pl-1.5 ${isDragging ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' : 'border-gray-200 dark:border-white/10 focus-within:border-violet-400/50'}`}>
          
          <input 
            type="file" 
            multiple 
            hidden 
            ref={fileInputRef} 
            onChange={handleFileChange} 
          />

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center justify-center h-9 w-9 rounded-full text-gray-400 hover:text-foreground hover:bg-gray-100 dark:hover:bg-white/10 transition-all shrink-0 mb-0.5"
            aria-label="Прикрепить файл"
          >
            <Paperclip size={20} />
          </motion.button>

          <textarea
            ref={textareaRef}
            value={vm.draftText}
            onChange={(e) => vm.setDraftText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isDragging ? "Отпустите файлы для загрузки..." : t("inputPlaceholder")}
            rows={1}
            className="flex-1 resize-none bg-transparent py-2.5 text-[15px] leading-6 text-foreground placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none min-h-[44px]"
            style={{ maxHeight: "200px" }}
          />

          <div className="flex items-center gap-1 shrink-0 pb-0.5 pr-0.5">
            <motion.button
              onClick={vm.isGenerating ? onStop : handleSend}
              disabled={!vm.isGenerating && !hasContent}
              className={`flex items-center justify-center h-9 w-9 rounded-full font-bold transition-all duration-200 shrink-0 ${
                vm.isGenerating
                  ? "bg-red-500 hover:bg-red-600 text-white shadow-[0_0_15px_rgba(239,68,68,0.4)]"
                  : hasContent
                    ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-[0_0_15px_rgba(168,85,247,0.4)]"
                    : "bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-gray-600"
              }`}
            >
              {vm.isGenerating ? <Square size={14} fill="currentColor" /> : <Send size={16} className="ml-0.5" />}
            </motion.button>
          </div>
        </div>
        
        {isDragging && (
           <div className="absolute inset-0 border-2 border-dashed border-violet-500 rounded-3xl pointer-events-none flex items-center justify-center bg-violet-500/5">
             <span className="text-violet-500 font-medium text-sm">Перетащите файлы сюда</span>
           </div>
        )}
      </div>
    </div>
  );
});